import { Columns } from "@fullscript/aviary-web";
import { useTranslation } from "react-i18next";

import { Arrange, Typography, Box, Link, Spacer } from "@aviary";
import { ProductImage } from "@aviary/molecules";
import { l } from "@clinic/emerson/locales";
import { emersonRoutes } from "@emerson/data/emersonRoutes";
import type { FeaturedProductsEmersonQuery } from "@emerson/data/queries/FeaturedProductsQuery/FeaturedProducts.query.generated";
import { useFeaturedProductsEmersonQuery } from "@emerson/data/queries/FeaturedProductsQuery/FeaturedProducts.query.generated";
import type { NarrowAt } from "@shared/types/codegenHelpers";

import { MockFeaturedProducts } from "./MockFeaturedProducts";

import * as styles from "./FeaturedProducts.styles";

export type EmersonFeaturedProduct = NarrowAt<FeaturedProductsEmersonQuery, ["viewer", "product"]>;

interface Props {
  productIds?: string[];
}

const FeaturedProducts = ({ productIds }: Props) => {
  const { t } = useTranslation();

  const { loading, error, data } = useFeaturedProductsEmersonQuery({
    variables: {
      productIds: productIds ?? [],
    },
    skip: !productIds?.length,
  });

  const getActionUrl = (product: EmersonFeaturedProduct): string => {
    return emersonRoutes.getPDP(
      product?.brandName,
      product?.slug ?? "",
      product?.id,
      product?.availableOrMasterVariant?.emersonVariantId ?? ""
    );
  };

  let featuredProducts: EmersonFeaturedProduct[] = MockFeaturedProducts;
  if (!error && !!productIds?.length) {
    featuredProducts = loading
      ? MockFeaturedProducts
      : data?.viewer?.product?.slice(0, 3) || MockFeaturedProducts;
  }

  const renderProducts = () => {
    return (
      <Columns>
        {featuredProducts.map((product, index) => (
          <Columns.Column key={`product-${index}`} columnWidth={4} phoneWidth={6}>
            <Box css={styles.FeaturedProductBox}>
              <Arrange justify="center" isFullWidth isFullHeight isVertical>
                <Link to={getActionUrl(product)} rel="stylesheet" css={styles.FeaturedProductLink}>
                  <ProductImage name={product?.brandName} image={product?.image} size="xlarge" />
                </Link>
                <Arrange isVertical>
                  <Link to={getActionUrl(product)} rel="stylesheet" isUnderLined={false}>
                    <Typography type="h4">{product?.brandName}</Typography>
                  </Link>
                  <Typography type="body" isFullWidth>
                    {product?.name}
                  </Typography>
                </Arrange>
              </Arrange>
            </Box>
          </Columns.Column>
        ))}
      </Columns>
    );
  };

  return (
    <>
      <Spacer height="double" />
      <Typography type="h2">{t(l.homepage.SectionTitles.FeaturedProducts)}</Typography>
      {renderProducts()}
      <Spacer height="double" />
    </>
  );
};

export { FeaturedProducts };
